<template>
  <header
    class="the-header"
    :class="headerBrandClass"
  >
    <div
      v-if="activeBrand"
      class="the-header__brand"
    >
      <ElementDropdown
        v-if="hasMultipleBrands"
        class="the-header__brand-dropdown"
        placement="bottom-start"
      >
        <ElementButton
          type="text"
          class="the-header__brand-selector"
        >
          <span class="the-header__brand-logo">
            <img
              :src="getBrandLogo(activeBrand)"
              :alt="activeBrand.name"
            >
          </span>

          <span class="the-header__brand-name">
            {{ activeBrand.name }}
          </span>

          <i class="el-icon-arrow-down" />
        </ElementButton>

        <ElementDropdownMenu
          slot="dropdown"
          class="the-header__brand-menu"
        >
          <ElementDropdownItem
            v-for="brand in availableBrands"
            :key="brand.id"
            :disabled="brand.id === activeBrand.id"
            class="the-header__brand-option"
            :class="{
              'the-header__brand-option--active': brand.id === activeBrand.id
            }"
            @click.native="switchBrand(brand)"
          >
            <span class="the-header__brand-option-logo">
              <img
                :src="getBrandLogo(brand)"
                :alt="brand.name"
              >
            </span>

            <span class="the-header__brand-option-name">
              {{ brand.name }}
            </span>

            <i
              v-if="brand.id === activeBrand.id"
              class="el-icon-check the-header__brand-option-check"
            />
          </ElementDropdownItem>
        </ElementDropdownMenu>
      </ElementDropdown>

      <div
        v-else
        class="the-header__brand-single"
      >
        <span class="the-header__brand-logo">
          <img
            :src="getBrandLogo(activeBrand)"
            :alt="activeBrand.name"
          >
        </span>

        <span class="the-header__brand-name">
          {{ activeBrand.name }}
        </span>
      </div>
    </div>

    <div class="the-header__center">
      <GroupsControl v-if="hasPermissions && visibleInCurrentPage" />

      <router-link
        v-else-if="visibleInCurrentPage"
        :to="{ path: '/library/' }"
        class="the-header__logo"
      >
        <h2 class="the-header__logo-text">
          Design<span>HUB</span>
        </h2>
      </router-link>

      <h2
        v-else
        class="the-header__logo-text"
      >
        {{ $route.meta.title }}
      </h2>
    </div>

    <div class="the-header__right">
      <ElementPopover
        v-if="hasPermissions"
        title="Notifications"
        trigger="click"
        :append-to-body="false"
        :visible-arrow="false"
        placement="bottom-end"
        popper-class="the-header__notifications"
        width="550"
      >
        <ElementBadge
          id="the-header__notification-count"
          slot="reference"
          :value="unseenNotificationsCount"
          :hidden="unseenNotificationsCount === 0 || !badgeVisibility"
          class="the-header__notification-badge"
        >
          <ElementButton
            type="text"
            class="the-header__icon-button the-header__notification-button"
            aria-label="Open notifications"
            @click="openNotificationsPopover"
          >
            <i class="el-icon-bell" />
          </ElementButton>
        </ElementBadge>

        <div v-if="notifications.length === 0">
          <div class="the-header__notification-empty">
            <i class="el-icon-wind-power" />
            <h3>There's nothing to see.</h3>
          </div>
        </div>

        <div
          v-else
          v-infinite-scroll="fetchNextNotifications"
          infinite-scroll-distance="500"
          :infinite-scroll-disabled="hasReachedEnd"
        >
          <Notifications :notifications="notifications" />
          <StitchLoader v-if="isLoadingNotifications" />
        </div>
      </ElementPopover>

      <ElementDropdown
        class="the-header__settings"
        placement="bottom-end"
      >
        <ElementButton
          type="text"
          class="the-header__icon-button the-header__settings-button"
          aria-label="Open account settings"
        >
          <i class="el-icon-setting" />
        </ElementButton>

        <ElementDropdownMenu
          slot="dropdown"
          class="the-header__settings-menu"
        >
          <ElementDropdownItem class="the-header__user-details">
            <span class="the-header__user-details-email">
              {{ user.email }}
            </span>
            <br>
            <span class="the-header__user-details-role">
              {{ getUserRole() }}
            </span>
          </ElementDropdownItem>

          <ElementDropdownItem
            v-if="hasPermissions"
            data-testid="notificationsSettingsButton"
            @click.native="openNotificationsSettings"
          >
            Settings
          </ElementDropdownItem>

          <ElementDropdownItem
            class="the-header__logout"
            divided
            @click.native="logout(user, $hubApiBaseUrl, $rendersApiBaseUrl)"
          >
            Logout
          </ElementDropdownItem>
        </ElementDropdownMenu>
      </ElementDropdown>
    </div>
  </header>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import GroupsControl from '../GroupsControl'
import Notifications from './components/notifications/index.vue'
import { USER_ROLE } from '@/constants/roleType'
import { DataUtils } from '@/mixins/utils'
import { FORM_NAME } from '@/constants/formName'
import { logout } from '@/services/utils'
import { ICON_TYPE } from '@/constants/dropdownOptions'
import { ACTIVE_BRAND_SESSION_KEY } from '@/constants/platform'
import _debounce from 'lodash/debounce'
import StitchLoader from '@/components/common/StitchLoader.vue'

import ckLogo from '@/assets/images/ck-icon.png'
import thLogo from '@/assets/images/th-icon.png'

export default {
  name: 'TheHeader',

  components: {
    StitchLoader,
    GroupsControl,
    Notifications
  },

  mixins: [DataUtils],

  data: () => ({
    USER_ROLE,
    FORM_NAME,
    ICON_TYPE,
    showWorkspaces: false,
    isLoadingNotifications: false,
    showNotificationsSettings: false,
    newInviteeList: [],

    brandLogos: {
      CK: ckLogo,
      TH: thLogo
    }
  }),

  computed: {
    ...mapGetters({
      user: 'getCognitoUserData',
      activeTeam: 'getActiveTeam',
      activeBrand: 'getActiveBrand',
      availableBrands: 'getAvailableBrands',
      notifications: 'getNotifications',
      unseenNotificationsCount: 'getUnseenNotificationsCount',
      totalNotificationsCount: 'getNotificationsCount',
      currentNotificationsPage: 'getCurrentNotificationsPage',
      badgeVisibility: 'getNotificationsBadgeVisibility',
      members: 'getMentionableUsers'
    }),

    /**
     * Returns the brand-specific header theme class.
     *
     * @returns {string}
     */
    headerBrandClass () {
      if (!this.activeBrand || !this.activeBrand.code) {
        return ''
      }

      const brandCode = String(this.activeBrand.code).toLowerCase()

      if (brandCode.indexOf('th') === 0) {
        return 'the-header--th'
      }

      if (brandCode.indexOf('ck') === 0) {
        return 'the-header--ck'
      }

      return ''
    },

    /**
     * @returns {boolean}
     */
    hasMultipleBrands () {
      return this.availableBrands.length > 1
    },

    /**
     * @returns {boolean}
     */
    hasReachedEnd () {
      return this.notifications.length === this.totalNotificationsCount
    },

    /**
     * @returns {boolean}
     */
    hasPermissions () {
      return Boolean(this.user.teams && this.user.teams.length > 0)
    },

    /**
     * @returns {boolean}
     */
    isVendorUser () {
      return this.user.role === USER_ROLE.VENDOR.name
    },

    /**
     * @returns {number}
     */
    itemId () {
      return this.getParamId()
    },

    /**
     * @returns {boolean}
     */
    visibleInCurrentPage () {
      return !['StyleDetails', 'Settings', 'AdminTools', 'FAQpage'].includes(
        this.$route.name
      )
    }
  },

  async mounted () {
    this.isLoadingNotifications = true
    await this.fetchNotifications()
    this.isLoadingNotifications = false
  },

  methods: {
    ...mapActions([
      'fetchNotifications',
      'changeBadgeVisibility',
      'markAllNotificationsSeen'
    ]),

    /**
     * Returns the logo for a brand.
     *
     * @param   {object} brand
     *
     * @returns {string}
     */
    getBrandLogo (brand) {
      return this.brandLogos[brand.code] || ''
    },

    /**
     */
    fetchNextNotifications: _debounce(
      /**
       */
      async function () {
        if (!this.hasReachedEnd) {
          this.isLoadingNotifications = true
          await this.fetchNotifications({
            page: this.currentNotificationsPage
          })
          this.isLoadingNotifications = false
        }
      },
      400
    ),

    /**
     * Extracts the user roles and permissions as a formatted string.
     *
     * @returns {string} A string representing the user's roles, including
     *                   Hub Admin, Vendor Admin, and the vendor company name if applicable.
     */
    getUserRole () {
      const roles = []

      roles.push(this.USER_ROLE[this.user.role.toUpperCase()].displayName)

      if (this.user.is_hub_admin) {
        roles.push('- Hub Admin')
      } else if (this.user.is_vendor_admin) {
        roles.push('- Vendor Admin')
      } else if (this.isVendorUser && this.user.vendor_company) {
        roles.push(`- ${this.user.vendor_company.name}`)
      }

      return roles.join(' ')
    },

    /**
     * Switches the active brand for the current tab.
     *
     * @param {object} brand
     */
    switchBrand (brand) {
      if (!brand || brand.id === this.activeBrand.id) {
        return
      }

      sessionStorage.setItem(ACTIVE_BRAND_SESSION_KEY, brand.code)

      window.location.assign(`/styles?brand=${brand.code}&group_id=all`)
    },

    /**
     */
    async openNotificationsPopover () {
      if (this.unseenNotificationsCount > 0) {
        await this.markAllNotificationsSeen()
      }

      this.changeBadgeVisibility({ visible: false })
    },

    /**
     */
    openNotificationsSettings () {
      this.$router.push({ name: 'Settings' })
    },

    /**
     */
    logout: logout
  }
}
</script>

<style lang="scss" scoped>
$header-access-manager-width: 100%;
$dialog-button-width: 200px;
$active-workspace-margin: 10px;
$logo-size: 32px;
$header-right-font-size: 20px;
$header-accent-height: 8px;
$header-selector-border: #d9dfe7;
$header-selector-shadow: 0 2px 8px rgba(15, 32, 56, 0.07);
$th-navy: #00174f;
$th-red: #d5002b;
$ck-charcoal: #15171a;
$ck-light-grey: #d8d6d2;
$header-accent-z-index: 1;
$header-horizontal-padding: 18px;
$header-aside-collapsed-width: spacing(9.5); // 76px;
$header-content-z-index: 2;
$header-ck-decoration-width: 42%;
$header-selector-min-height: 36px;
$header-selector-max-height: 48px;
$header-icon-muted: #97a2af;
$header-accent-content-gap: 4px;

.the-header {
  position: relative;
  z-index: $z-index-layer-1;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: percentage(100);
  height: $main-header-height;
  padding: 0 $header-horizontal-padding;
  overflow: visible;
  background-color: $white;
  box-shadow: 0 5px 18px rgba(25, 39, 58, 0.09);
  transition: background-color $transition, box-shadow $transition;
}

.the-header--th {
  background-color: $white;

  &::before {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    z-index: $header-accent-z-index;
    height: $header-accent-height;
    background:
      linear-gradient(
        90deg,
        $th-navy 0%,
        $th-navy 33.333%,
        $th-red 33.333%,
        $th-red 66.666%,
        $white 66.666%,
        $white 100%
      );
    content: '';
    pointer-events: none;
  }
}

.the-header--ck {
  background:
    linear-gradient(
      90deg,
      #f1f2f3 0%,
      #f7f7f7 24%,
      $white 49%,
      #f4f5f6 73%,
      #eceeef 100%
    );

  &::before {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    z-index: $header-accent-z-index;
    height: $header-accent-height;
    background:
      linear-gradient(
        90deg,
        $ck-charcoal 0%,
        $ck-charcoal 33.333%,
        $ck-light-grey 33.333%,
        $ck-light-grey 66.666%,
        $white 66.666%,
        $white 100%
      );
    content: '';
    pointer-events: none;
  }

  &::after {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 0;
    width: $header-ck-decoration-width;
    height: percentage(100);
    background:
      linear-gradient(
        151deg,
        transparent 0%,
        transparent 36%,
        rgba(71, 77, 84, 0.04) 36.5%,
        rgba(111, 117, 124, 0.15) 44%,
        rgba(255, 255, 255, 0.8) 50%,
        rgba(115, 121, 128, 0.12) 57%,
        rgba(255, 255, 255, 0.72) 63%,
        rgba(78, 84, 91, 0.09) 70%,
        transparent 80%,
        transparent 100%
      );
    filter: blur(1px);
    content: '';
    pointer-events: none;
  }
}

.the-header__brand,
.the-header__center,
.the-header__right {
  position: relative;
  z-index: $header-content-z-index;
  margin-top: $header-accent-content-gap;
}

.the-header__brand {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  height: percentage(100);
}

.the-header__brand-dropdown {
  display: flex;
  align-items: center;
  height: percentage(100);
}

.the-header__brand-selector,
.the-header__brand-single {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: clamp(220px, 13vw, 270px);
  height: calc(100% - 12px);
  min-height: $header-selector-min-height;
  max-height: $header-selector-max-height;
  padding: 0;
  color: $grey-dark;
  line-height: 1;
  outline: none;
  transition:
    border-color $transition,
    box-shadow $transition,
    transform $transition;
}

.the-header__brand-logo,
.the-header__brand-option-logo {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  img {
    display: block;
    max-width: percentage(100);
    max-height: percentage(100);
    object-fit: contain;
  }
}

.the-header__brand-logo {
  width: spacing(6);
  height: spacing(4);
  margin-right: spacing(2);
}

.the-header--th .the-header__brand-logo {
  width: spacing(6);
  height: spacing(3.5);
}

.the-header--ck .the-header__brand-logo {
  width: spacing(5.5);
  height: spacing(4);
}

.the-header__brand-name,
.the-header__brand-option-name {
  overflow: hidden;
  color: $ck-charcoal;
  font-weight: $font-weight-semibold;
  font-size: spacing(1.6);
  line-height: 1;
  letter-spacing: spacing(0.02);
  white-space: nowrap;
  text-transform: uppercase;
  text-overflow: ellipsis;
}

.the-header__brand-name {
  display: inline-flex;
  align-items: center;
  align-self: center;
  height: spacing(4);
}

.the-header__brand-selector .el-icon-arrow-down {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  align-self: center;
  justify-content: center;
  margin-left: auto;
  color: $header-icon-muted;
  font-weight: $font-weight-semibold;
  font-size: spacing(1.5);
  line-height: 1;
}

.the-header__brand-option-logo {
  width: spacing(6);
  height: spacing(4);
  margin-right: spacing(2);
}

.the-header__brand-option-check {
  margin-left: auto;
  color: $blue;
}

.the-header__center {
  position: absolute;
  top: percentage(50);
  left: percentage(50);
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translate(-50%, -50%);
}

.the-header__workspace {
  display: flex;
  align-items: center;
}

.the-header__workspace .the-header__workspace-logo--active {
  width: $logo-size;
  height: $logo-size;
  margin-left: $active-workspace-margin;
}

.the-header__workspace-item {
  display: flex;
  align-items: center;
}

.the-header__workspace .the-header__workspace-list-item-logo {
  width: $logo-size;
  height: $logo-size;
  margin-right: $active-workspace-margin;
}

.the-header__logo-text {
  margin: 0;
  line-height: 1;
  transition: color $transition;

  span {
    font-weight: $font-weight-bold;
  }
}

.the-header__logo:hover .the-header__logo-text {
  color: $blue;
}

.the-header__right {
  display: flex;
  flex-shrink: 0;
  gap: spacing(0.2);
  align-items: center;
  margin-left: auto;
}

.the-header__icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: spacing(4);
  height: spacing(4);
  padding: 0;
  color: $blue;
  border-radius: percentage(50);
  transition: color $transition, background-color $transition;

  i {
    margin: 0;
    font-weight: $font-weight-bold;
    font-size: $header-right-font-size;
    line-height: 1;
  }

  &:hover,
  &:focus {
    color: $blue;
    background-color: rgba($blue, 0.08);
  }
}

.the-header--th .the-header__icon-button {
  color: $blue;
}

.the-header--ck .the-header__notification-button {
  color: $blue;
}

.the-header--ck .the-header__settings-button {
  &:hover,
  &:focus {
    color: $black;
    background-color: rgba($black, 0.06);
  }
}

.the-header__right .el-dropdown-menu__item {
  white-space: nowrap;
}

.the-header__user-details {
  line-height: 12px;
}

.the-header__user-details:hover {
  color: inherit !important;
  background-color: transparent !important;
  cursor: unset;
}

.the-header__user-details:focus {
  outline: none;
  box-shadow: none;
  cursor: unset;
}

.the-header__user-details-email {
  position: relative;
}

.the-header__user-details-role {
  position: relative;
  color: $grey-light;
  font-weight: $font-weight-medium;
  font-size: spacing(1.2);
}

.header-access-manager__loader {
  @include position-absolute(center);

  width: percentage(100);
  height: percentage(100);
  background-color: $white-overlay;
  transition: opacity $transition-duration-2;
}

.header-access-manager__content {
  width: $header-access-manager-width;
}

.the-header__dialog-button {
  width: $dialog-button-width;
}

.the-header__logout {
  &,
  &:hover {
    color: $red;
  }
}

.the-header__notification-empty {
  margin: spacing(10) 0;
  text-align: center;
}

.the-header__notification-empty i {
  margin-bottom: spacing(3/2);
  font-size: spacing(6);
  opacity: 0.5;
}

@media (max-width: 980px) {
  .the-header {
    padding-right: spacing(3);
    padding-left: spacing(3);
  }

  .the-header__brand-selector,
  .the-header__brand-single {
    width: auto;
    min-width: spacing(8);
  }

  .the-header__brand-name {
    display: none;
  }

  .the-header__brand-logo {
    margin-right: 0;
  }

  .the-header__brand-selector .el-icon-arrow-down {
    margin-left: spacing(1);
  }
}
</style>

<style lang="scss">
$notification-badge-size: 10px;
$popover-padding: 8px 8px 0 12px;
$brand-menu-min-width: 220px;
$brand-menu-option-hover-background: #f4f7fa;
$brand-menu-option-active-background: #eef6ff;
$brand-menu-option-text: #15171a;
$settings-menu-viewport-gap: spacing(2);

.the-header__settings-menu.el-dropdown-menu {
  right: $settings-menu-viewport-gap !important;
  left: auto !important;
  max-width: calc(100vw - #{spacing(4)});
}

.the-header__brand-selector > span {
  display: flex;
  align-items: center;
  width: percentage(100);
  height: percentage(100);
  line-height: 1;
}

.el-badge {
  vertical-align: unset;
}

.el-popover__title {
  padding: $popover-padding;
  color: $grey-dark;
  font-weight: $font-weight-semibold;
  font-size: spacing(3);
  text-transform: capitalize;
}

.the-header__brand-menu.el-dropdown-menu {
  box-sizing: border-box;
  min-width: $brand-menu-min-width;
  padding: spacing(0.75);
  border: 1px solid #e2e6eb;
  border-radius: spacing(1);
  box-shadow: 0 10px 28px rgba(21, 33, 50, 0.14);
}

.the-header__brand-menu .el-dropdown-menu__item {
  display: flex;
  align-items: center;
  min-height: spacing(6);
  padding: 0 spacing(1.5);
  line-height: 1;
  border-radius: spacing(0.75);
}

.the-header__brand-menu .el-dropdown-menu__item:hover,
.the-header__brand-menu .el-dropdown-menu__item:focus {
  color: $grey-dark;
  background-color: $brand-menu-option-hover-background;
}

.the-header__brand-menu .the-header__brand-option--active,
.the-header__brand-menu .the-header__brand-option--active.is-disabled {
  color: $grey-dark;
  background-color: $brand-menu-option-active-background;
  cursor: default;
  opacity: 1;
}

.the-header__brand-menu .the-header__brand-option-logo {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: spacing(6);
  height: spacing(4);
  margin-right: spacing(2);
}

.the-header__brand-menu .the-header__brand-option-logo img {
  display: block;
  max-width: percentage(100);
  max-height: percentage(100);
  object-fit: contain;
}

.the-header__brand-menu .the-header__brand-option-name {
  overflow: hidden;
  color: $brand-menu-option-text;
  font-weight: $font-weight-semibold;
  font-size: spacing(1.6);
  line-height: 1;
  white-space: nowrap;
  text-transform: uppercase;
  text-overflow: ellipsis;
}

.the-header__brand-menu .the-header__brand-option-check {
  margin-left: auto;
  color: $blue;
}

.the-header__notifications {
  max-height: spacing(56);
  overflow: scroll;
  overflow-x: hidden;
  box-shadow: $shadow-default;
}

#the-header__notification-count .el-badge__content {
  top: 0;
  right: spacing(2);
  min-width: spacing(2);
  height: spacing(2);
  padding: 0 spacing(0.5);
  font-size: $notification-badge-size;
  line-height: spacing(2);
}
</style>
